import { useId, useState } from 'react';
import { dutyAnalyticsWindowRefusal } from '@pams/shared';
import { DatePicker, type DateTime } from '@/components/DatePicker';
import { FilterField } from '@/components/FilterField';
import type { CustomWindow } from '@/lib/dutyAnalytics';
import { asPickerDate, fromPickerDate } from '@/lib/pickerDate';
import { DUTY_ANALYTICS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C.1 §5] THE CUSTOM RANGE'S TWO DATES — shown only while "Custom" is chosen, in the page header's filter row.
 *
 *  ⚠️ A DRAFT, AND THE URL ONLY EVER HOLDS A PAIR THE RULE ACCEPTS. A pick updates the draft; a draft the shared rule
 *  accepts is written to the URL, which is the request. A draft it refuses — reversed, ending after today, or over
 *  thirty days — shows the shared sentence under the pickers, tied to both by `aria-describedby`, and asks nothing:
 *  the figures stay those of the last pair asked for. The page keys this component by that pair, so a URL change
 *  resets the draft with no effect.
 *
 *  ⚠️ `disabledDate` REFUSES ONLY THE DAYS AFTER TODAY, AS A CALENDAR-DAY STRING (§5d). Never an instant comparison,
 *  and never a rule relative to the other picker: the span cap lives in the shared rule alone, and two pickers that
 *  constrain each other can trap a person with no way to move either. `today` is the page's `istNow()` day.
 *
 *  ⚠️ NO CLEAR CONTROL: a custom range is two dates or it is a preset, and the presets sit beside it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;

/** The house display format — the Dashboard's pickers use it. The value behind it is the ISO calendar day. */
const PICKER_FORMAT = 'dd LLL yyyy';

export const CustomRange = ({
  value,
  today,
  onChange,
}: {
  value: CustomWindow;
  /** The IST calendar day, `YYYY-MM-DD`. */
  today: string;
  onChange: (next: CustomWindow) => void;
}) => {
  const [draft, setDraft] = useState<CustomWindow>(value);
  const refusalId = useId();
  const refusal = dutyAnalyticsWindowRefusal(draft.dateFrom, draft.dateTo, today);

  const choose = (next: CustomWindow): void => {
    setDraft(next);
    if (dutyAnalyticsWindowRefusal(next.dateFrom, next.dateTo, today) === null) onChange(next);
  };

  const disabledDate = (cell: DateTime): boolean => (cell.toISODate() ?? '') > today;

  const picker = (key: keyof CustomWindow, label: string) => (
    <FilterField label={label}>
      <DatePicker
        className="w-36"
        format={PICKER_FORMAT}
        allowClear={false}
        value={asPickerDate(draft[key])}
        disabledDate={disabledDate}
        aria-describedby={refusal === null ? undefined : refusalId}
        onChange={(next) => {
          const date = fromPickerDate(next);
          if (date !== null) choose({ ...draft, [key]: date });
        }}
      />
    </FilterField>
  );

  return (
    <div
      role="group"
      aria-label={copy.customRangeLabel}
      data-testid="duty-analytics-custom-range"
      className="flex flex-col gap-2"
    >
      <div className="flex flex-wrap items-end gap-3">
        {picker('dateFrom', copy.customFrom)}
        {picker('dateTo', copy.customTo)}
      </div>
      {refusal !== null && (
        <p id={refusalId} role="alert" className="text-sm text-destructive" data-testid="duty-analytics-range-refusal">
          {refusal}
        </p>
      )}
    </div>
  );
};
